/**
 * Webtech HR Engine™ - Performance Module Page (Cockpit RH)
 * Vue Admin RH avec KPIs, IA, et pilotage avancé des évaluations
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import api from '../lib/api';
import CampaignWizard from '../components/performance/CampaignWizard';
import {
  Target,
  Plus,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  Play,
  Archive,
  BarChart3,
  Eye,
  UserCheck,
  Star,
  MoreVertical,
  Trash2,
  Copy,
  Download,
  Building2,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  MessageSquare,
  ArrowUpDown,
  Filter,
  AlertTriangle,
  TrendingUp,
  Sparkles,
  Brain,
  Zap,
  FileText,
  Send,
  RefreshCw,
  XCircle,
  CheckCircle,
  AlertCircle,
  Activity,
  PieChart,
  Crown,
  ArrowRight,
  Info,
  Gauge
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
  draft: { 
    label: 'Brouillon', 
    color: 'bg-slate-100 text-slate-700',
    dotColor: 'bg-slate-400',
    icon: FileText,
    action: 'Finaliser & activer',
    actionColor: 'bg-green-600 hover:bg-green-700'
  },
  active: { 
    label: 'En cours', 
    color: 'bg-green-100 text-green-700',
    dotColor: 'bg-green-500',
    icon: Play,
    action: 'Relancer managers',
    actionColor: 'bg-blue-600 hover:bg-blue-700'
  },
  late: { 
    label: 'En retard', 
    color: 'bg-red-100 text-red-700',
    dotColor: 'bg-red-500',
    icon: AlertTriangle,
    action: 'Identifier blocages',
    actionColor: 'bg-amber-600 hover:bg-amber-700'
  },
  closed: { 
    label: 'Terminée', 
    color: 'bg-blue-100 text-blue-700',
    dotColor: 'bg-blue-500',
    icon: CheckCircle2,
    action: 'Rapport Direction',
    actionColor: 'bg-purple-600 hover:bg-purple-700'
  },
  cancelled: { 
    label: 'Annulée', 
    color: 'bg-red-100 text-red-700',
    dotColor: 'bg-red-500',
    icon: XCircle,
    action: null,
    actionColor: ''
  }
};

// Campaign type badges
const CAMPAIGN_TYPE = {
  '180': { label: '180°', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  '360': { label: '360°', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'self': { label: 'Auto', color: 'bg-slate-50 text-slate-700 border-slate-200' }
};

// Helper to detect campaign type from name or type field
const getCampaignType = (campaign) => {
  if (campaign.type) return campaign.type;
  if (campaign.name?.toLowerCase().includes('360')) return '360';
  if (campaign.name?.toLowerCase().includes('180')) return '180';
  return '180'; // Default
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, bgColor, subtitle, trend, onClick, active }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-md ${active ? 'ring-2 ring-webtech-blue' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        {trend && (
          <Badge variant="outline" className={trend > 0 ? 'text-green-600' : 'text-red-600'}>
            {trend > 0 ? '+' : ''}{trend}%
          </Badge>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-3">{value}</p>
      <p className="text-sm text-slate-500">{title}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

// Global Status Badge
const GlobalStatusBadge = ({ campaigns }) => {
  const active = campaigns.filter(c => c.status === 'active').length;
  const late = campaigns.filter(c => {
    if (c.status !== 'active') return false;
    return new Date(c.endDate) < new Date();
  }).length;
  
  if (campaigns.length === 0) {
    return (
      <Badge className="bg-slate-100 text-slate-600 px-3 py-1">
        <Activity className="w-3 h-3 mr-1" />
        Aucune campagne
      </Badge>
    );
  }
  
  if (late > 0) {
    return (
      <Badge className="bg-red-100 text-red-700 px-3 py-1">
        <AlertTriangle className="w-3 h-3 mr-1" />
        À risque ({late} en retard)
      </Badge>
    );
  }
  
  if (active > 0) {
    return (
      <Badge className="bg-green-100 text-green-700 px-3 py-1">
        <CheckCircle className="w-3 h-3 mr-1" />
        OK - {active} active(s)
      </Badge>
    );
  }
  
  return (
    <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
      <AlertCircle className="w-3 h-3 mr-1" />
      Aucune campagne active
    </Badge>
  );
};

// AI Insights Card for Performance
const AIPerformanceInsights = ({ campaigns, employees, isPro, onCreateRecommended }) => {
  const [loading, setLoading] = useState(false);
  
  // Generate contextual insights
  const getInsights = () => {
    const active = campaigns.filter(c => c.status === 'active');
    const drafts = campaigns.filter(c => c.status === 'draft');
    const completed = campaigns.filter(c => c.status === 'closed');
    const totalParticipants = campaigns.reduce((sum, c) => sum + (c.participants?.length || 0), 0);
    
    const insights = [];
    
    if (campaigns.length === 0) {
      insights.push({
        type: 'critical',
        icon: AlertTriangle,
        message: "Aucune campagne d'évaluation créée",
        detail: "L'absence de processus d'évaluation impacte la gestion des talents et la motivation des équipes.",
        action: "Créer une première campagne"
      });
    } else if (active.length === 0 && drafts.length > 0) {
      insights.push({
        type: 'warning',
        icon: Clock,
        message: `${drafts.length} campagne(s) en brouillon non activée(s)`,
        detail: "Des campagnes sont prêtes mais non lancées. Activez-les pour commencer les évaluations.",
        action: "Activer les campagnes"
      });
    } else if (active.length === 0) {
      insights.push({
        type: 'warning',
        icon: AlertCircle,
        message: "Aucune campagne active actuellement",
        detail: "Planifiez régulièrement des évaluations pour maintenir l'engagement des équipes.",
        action: "Créer une campagne"
      });
    }
    
    if (active.length > 0) {
      // Calculate real completion rate from active campaigns (using enriched data from backend)
      let totalParticipantsInActive = 0;
      let completedParticipants = 0;
      
      active.forEach(campaign => {
        // Use enriched data from backend API
        totalParticipantsInActive += campaign.participantsCount || 0;
        completedParticipants += campaign.completedCount || 0;
      });
      
      const avgCompletion = totalParticipantsInActive > 0 
        ? Math.round((completedParticipants / totalParticipantsInActive) * 100) 
        : 0;
      
      if (avgCompletion < 50 && totalParticipantsInActive > 0) {
        insights.push({
          type: 'warning',
          icon: TrendingUp,
          message: `Taux de complétion faible (${avgCompletion}%)`,
          detail: `${completedParticipants}/${totalParticipantsInActive} évaluations complétées. Certains managers n'ont pas encore finalisé.`,
          action: "Relancer les managers"
        });
      } else if (avgCompletion >= 50 && avgCompletion < 80 && totalParticipantsInActive > 0) {
        insights.push({
          type: 'info',
          icon: TrendingUp,
          message: `Taux de complétion en cours (${avgCompletion}%)`,
          detail: `${completedParticipants}/${totalParticipantsInActive} évaluations complétées. Les campagnes progressent bien.`,
          action: "Voir le détail"
        });
      } else if (avgCompletion >= 80 && totalParticipantsInActive > 0) {
        insights.push({
          type: 'success',
          icon: CheckCircle,
          message: `Excellent taux de complétion (${avgCompletion}%)`,
          detail: `${completedParticipants}/${totalParticipantsInActive} évaluations finalisées. Félicitations !`,
          action: null
        });
      } else if (totalParticipantsInActive === 0) {
        insights.push({
          type: 'info',
          icon: Users,
          message: "Campagnes actives sans participants",
          detail: "Ajoutez des participants aux campagnes actives pour démarrer les évaluations.",
          action: "Ajouter des participants"
        });
      }
    }
    
    if (employees.length > 0 && totalParticipants < employees.length * 0.5) {
      insights.push({
        type: 'info',
        icon: Users,
        message: "Couverture partielle des effectifs",
        detail: `Seulement ${Math.round((totalParticipants / employees.length) * 100)}% des employés sont évalués.`,
        action: "Élargir les campagnes"
      });
    }
    
    if (insights.length === 0) {
      insights.push({
        type: 'success',
        icon: CheckCircle,
        message: "Processus d'évaluation bien piloté",
        detail: "Vos campagnes sont actives et progressent correctement.",
        action: null
      });
    }
    
    return insights;
  };
  
  const insights = getInsights();
  
  if (!isPro) {
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
              <Brain className="w-6 h-6 text-slate-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-700">Analyse IA Performance</p>
              <p className="text-sm text-slate-500">Passez au plan PRO pour accéder aux recommandations IA</p>
            </div>
            <Button variant="outline" className="gap-2">
              <Crown className="w-4 h-4 text-amber-500" />
              Passer PRO
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border-indigo-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            Synthèse IA - Performance RH
          </CardTitle>
          <Badge className="bg-indigo-100 text-indigo-700">
            <Zap className="w-3 h-3 mr-1" />
            PRO
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {insights.map((insight, idx) => (
          <div 
            key={idx} 
            className={`p-3 rounded-lg border ${
              insight.type === 'critical' ? 'bg-red-50 border-red-200' :
              insight.type === 'warning' ? 'bg-amber-50 border-amber-200' :
              insight.type === 'success' ? 'bg-green-50 border-green-200' :
              'bg-blue-50 border-blue-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <insight.icon className={`w-5 h-5 mt-0.5 ${
                insight.type === 'critical' ? 'text-red-600' :
                insight.type === 'warning' ? 'text-amber-600' :
                insight.type === 'success' ? 'text-green-600' :
                'text-blue-600'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-slate-900">{insight.message}</p>
                <p className="text-sm text-slate-600 mt-0.5">{insight.detail}</p>
              </div>
              {insight.action && (
                <Button size="sm" variant="outline" className="text-xs whitespace-nowrap">
                  {insight.action}
                </Button>
              )}
            </div>
          </div>
        ))}
        
        <div className="flex gap-2 pt-2">
          <Button 
            onClick={onCreateRecommended}
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Créer campagne recommandée
          </Button>
          <Button variant="outline" className="gap-2">
            <Brain className="w-4 h-4" />
            Recommandations détaillées
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Setup Guide Component for Performance
const PerformanceSetupGuide = ({ campaigns, employees, onNavigate, onCreateCampaign }) => {
  const hasEmployees = employees.length > 0;
  const hasCampaigns = campaigns.length > 0;
  const hasActiveCampaign = campaigns.some(c => c.status === 'active');
  const hasCompletedCampaign = campaigns.some(c => c.status === 'closed');
  
  const steps = [
    {
      id: 1,
      title: 'Ajouter des employés',
      description: 'Créez les fiches de vos collaborateurs',
      icon: Users,
      color: 'blue',
      completed: hasEmployees,
      count: employees.length,
      action: () => onNavigate('/employees'),
      buttonText: 'Ajouter employés'
    },
    {
      id: 2,
      title: 'Créer une campagne',
      description: 'Définissez votre première campagne d\'évaluation',
      icon: Target,
      color: 'amber',
      completed: hasCampaigns,
      count: campaigns.length,
      action: onCreateCampaign,
      buttonText: 'Créer campagne'
    },
    {
      id: 3,
      title: 'Activer la campagne',
      description: 'Lancez les évaluations auprès de vos équipes',
      icon: Play,
      color: 'green',
      completed: hasActiveCampaign,
      count: campaigns.filter(c => c.status === 'active').length,
      action: () => {},
      buttonText: 'Activer'
    },
    {
      id: 4,
      title: 'Analyser les résultats',
      description: 'Consultez les rapports et insights après clôture',
      icon: BarChart3,
      color: 'purple',
      completed: hasCompletedCampaign,
      count: campaigns.filter(c => c.status === 'closed').length,
      action: () => onNavigate('/ai-insights'),
      buttonText: 'Voir résultats'
    }
  ];
  
  const completedSteps = steps.filter(s => s.completed).length;
  const progress = (completedSteps / steps.length) * 100;
  
  const colorMap = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500' },
    green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', ring: 'ring-green-500' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', ring: 'ring-purple-500' },
  };
  
  return (
    <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              Guide de configuration
            </CardTitle>
            <CardDescription className="mt-1">
              Suivez ces étapes pour activer votre module Performance
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-amber-600">{completedSteps}/{steps.length}</p>
            <p className="text-xs text-slate-500">étapes complétées</p>
          </div>
        </div>
        <Progress value={progress} className="h-2 mt-4" />
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          {steps.map((step, idx) => {
            const colors = colorMap[step.color];
            const Icon = step.icon;
            const isNext = !step.completed && steps.slice(0, idx).every(s => s.completed);
            
            return (
              <div 
                key={step.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  step.completed 
                    ? 'bg-green-50 border-green-200' 
                    : isNext 
                      ? `bg-white ${colors.border} ring-2 ${colors.ring} ring-opacity-50` 
                      : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    step.completed ? 'bg-green-100' : colors.bg
                  }`}>
                    {step.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${step.completed ? 'text-green-700' : 'text-slate-900'}`}>
                        {step.title}
                      </p>
                      {step.completed && step.count > 0 && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {step.count}
                        </Badge>
                      )}
                      {isNext && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          À faire
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{step.description}</p>
                    
                    {!step.completed && (
                      <Button 
                        size="sm" 
                        className={`mt-3 ${isNext ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                        variant={isNext ? 'default' : 'outline'}
                        onClick={step.action}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {step.buttonText}
                      </Button>
                    )}
                    
                    {step.completed && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="mt-3 text-green-600"
                        onClick={step.action}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Campaign Card Component (improved) - Real data only
const CampaignCard = ({ campaign, employees, onActivate, onClose, onDelete, onRelance, onViewDetails }) => {
  const status = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  
  // Use participantsCount from backend (supports both old and new format)
  const total = campaign.participantsCount || campaign.participants?.length || 0;
  
  // Use completedCount from backend or stats.completed
  const completed = campaign.completedCount || campaign.stats?.completed || 0;
  
  // Use progress from backend or calculate
  const progress = campaign.progress || (total > 0 ? Math.round((completed / total) * 100) : 0);
  
  // Check if campaign is late
  const isLate = campaign.status === 'active' && new Date(campaign.endDate) < new Date();
  const displayStatus = isLate ? STATUS_CONFIG.late : status;
  
  // Use managersCount from backend or calculate
  const managersCount = campaign.managersCount || new Set(
    campaign.participants?.map(p => 
      employees.find(e => e.id === p.employeeId)?.managerId
    ).filter(Boolean)
  ).size;
  
  // Days remaining
  const daysRemaining = Math.ceil((new Date(campaign.endDate) - new Date()) / (1000 * 60 * 60 * 24));
  
  const handleContextualAction = (e) => {
    e.stopPropagation();
    switch(campaign.status) {
      case 'draft': onActivate(campaign.id, e); break;
      case 'active': onRelance(campaign); break;
      case 'closed': onViewDetails(campaign); break;
      default: break;
    }
  };
  
  return (
    <Card className={`hover:shadow-md transition-all cursor-pointer ${isLate ? 'border-red-300' : ''}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isLate ? 'bg-red-100' : 
              campaign.status === 'active' ? 'bg-green-100' :
              campaign.status === 'closed' ? 'bg-blue-100' :
              'bg-slate-100'
            }`}>
              <StatusIcon className={`w-5 h-5 ${
                isLate ? 'text-red-600' :
                campaign.status === 'active' ? 'text-green-600' :
                campaign.status === 'closed' ? 'text-blue-600' :
                'text-slate-600'
              }`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                <Badge variant="outline" className={CAMPAIGN_TYPE[campaign.type || '180']?.color}>
                  {CAMPAIGN_TYPE[campaign.type || '180']?.label}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">
                {new Date(campaign.startDate).toLocaleDateString('fr-FR')} → {new Date(campaign.endDate).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge className={displayStatus.color}>
              {displayStatus.label}
            </Badge>
            {isLate && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Date limite dépassée</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-4 py-3 bg-slate-50 rounded-lg px-3">
          <div className="text-center">
            <p className="text-lg font-bold text-slate-900">{total}</p>
            <p className="text-xs text-slate-500">Employés</p>
          </div>
          <div className="text-center border-l border-slate-200">
            <p className="text-lg font-bold text-slate-900">{managersCount}</p>
            <p className="text-xs text-slate-500">Managers</p>
          </div>
          <div className="text-center border-l border-slate-200">
            <p className="text-lg font-bold text-slate-900">{completed}/{total}</p>
            <p className="text-xs text-slate-500">Complétées</p>
          </div>
          <div className="text-center border-l border-slate-200">
            <p className={`text-lg font-bold ${daysRemaining < 0 ? 'text-red-600' : daysRemaining < 7 ? 'text-amber-600' : 'text-slate-900'}`}>
              {daysRemaining < 0 ? `+${Math.abs(daysRemaining)}j` : `${daysRemaining}j`}
            </p>
            <p className="text-xs text-slate-500">{daysRemaining < 0 ? 'Retard' : 'Restants'}</p>
          </div>
        </div>
        
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">Progression</span>
            <span className={`font-medium ${progress >= 80 ? 'text-green-600' : progress >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
              {progress}%
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {displayStatus.action && (
            <Button 
              size="sm" 
              className={`flex-1 text-white ${displayStatus.actionColor}`}
              onClick={handleContextualAction}
            >
              {campaign.status === 'draft' && <Play className="w-4 h-4 mr-1" />}
              {campaign.status === 'active' && <Send className="w-4 h-4 mr-1" />}
              {campaign.status === 'closed' && <FileText className="w-4 h-4 mr-1" />}
              {displayStatus.action}
            </Button>
          )}
          
          <Link to={`/performance/campaign/${campaign.id}`} onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="outline">
              <Eye className="w-4 h-4 mr-1" />
              Détails
            </Button>
          </Link>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem><Copy className="w-4 h-4 mr-2" />Dupliquer</DropdownMenuItem>
              <DropdownMenuItem><Download className="w-4 h-4 mr-2" />Export PDF</DropdownMenuItem>
              {campaign.status === 'active' && (
                <DropdownMenuItem onClick={() => onClose(campaign.id)}>
                  <Archive className="w-4 h-4 mr-2" />Clôturer
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(campaign); }}>
                <Trash2 className="w-4 h-4 mr-2" />Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

// Collaborators Table Component
const CollaboratorsTable = ({ campaigns, employees, departments }) => {
  const [filterManager, setFilterManager] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get all participants from all active campaigns
  const allParticipants = useMemo(() => {
    const participants = [];
    campaigns.filter(c => c.status === 'active' || c.status === 'closed').forEach(campaign => {
      (campaign.participants || []).forEach(p => {
        const employee = employees.find(e => e.id === p.employeeId);
        if (employee) {
          const manager = employees.find(e => e.id === employee.managerId);
          const dept = departments.find(d => d.id === employee.departmentId);
          participants.push({
            ...p,
            employee,
            manager,
            department: dept,
            campaignName: campaign.name,
            campaignId: campaign.id,
            status: p.status || 'pending'
          });
        }
      });
    });
    return participants;
  }, [campaigns, employees, departments]);
  
  // Get unique managers
  const managers = useMemo(() => {
    const managerIds = [...new Set(allParticipants.map(p => p.manager?.id).filter(Boolean))];
    return managerIds.map(id => employees.find(e => e.id === id)).filter(Boolean);
  }, [allParticipants, employees]);
  
  // Filter participants
  const filteredParticipants = useMemo(() => {
    return allParticipants.filter(p => {
      if (filterManager !== 'all' && p.manager?.id !== filterManager) return false;
      if (filterDept !== 'all' && p.department?.id !== filterDept) return false;
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = `${p.employee?.firstName} ${p.employee?.lastName}`.toLowerCase();
        if (!name.includes(query)) return false;
      }
      return true;
    });
  }, [allParticipants, filterManager, filterDept, filterStatus, searchQuery]);
  
  // Identify at-risk items
  const atRiskManagers = useMemo(() => {
    const managerStats = {};
    allParticipants.forEach(p => {
      if (p.manager) {
        if (!managerStats[p.manager.id]) {
          managerStats[p.manager.id] = { total: 0, completed: 0, manager: p.manager };
        }
        managerStats[p.manager.id].total++;
        if (p.status === 'completed') managerStats[p.manager.id].completed++;
      }
    });
    
    return Object.values(managerStats).filter(s => 
      s.total > 0 && (s.completed / s.total) < 0.3
    );
  }, [allParticipants]);
  
  return (
    <div className="space-y-4">
      {/* AI Alert for inactive managers */}
      {atRiskManagers.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {atRiskManagers.length} manager(s) avec faible taux de complétion
                </p>
                <p className="text-sm text-amber-700">
                  {atRiskManagers.map(m => `${m.manager.firstName} ${m.manager.lastName}`).join(', ')}
                </p>
              </div>
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300">
                <Send className="w-4 h-4 mr-1" />
                Relancer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Rechercher un collaborateur..." 
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <Select value={filterManager} onValueChange={setFilterManager}>
          <SelectTrigger className="w-[180px]">
            <UserCheck className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Manager" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les managers</SelectItem>
            {managers.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.firstName} {m.lastName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-[180px]">
            <Building2 className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Département" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les départements</SelectItem>
            {departments.map(d => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="completed">Complété</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Collaborateur</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Département</TableHead>
              <TableHead>Campagne</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredParticipants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  Aucun collaborateur trouvé
                </TableCell>
              </TableRow>
            ) : (
              filteredParticipants.slice(0, 20).map((p, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-webtech-blue text-white text-xs">
                          {p.employee?.firstName?.[0]}{p.employee?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-slate-900">{p.employee?.firstName} {p.employee?.lastName}</p>
                        <p className="text-xs text-slate-500">{p.employee?.jobTitle}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {p.manager ? (
                      <span className="text-slate-700">{p.manager.firstName} {p.manager.lastName}</span>
                    ) : (
                      <span className="text-slate-400">Non assigné</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-slate-600">{p.department?.name || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">{p.campaignName}</span>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      p.status === 'completed' ? 'bg-green-100 text-green-700' :
                      p.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }>
                      {p.status === 'completed' ? 'Complété' : p.status === 'in_progress' ? 'En cours' : 'En attente'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {filteredParticipants.length > 20 && (
          <div className="p-3 border-t text-center">
            <Button variant="link" className="text-webtech-blue">
              Voir les {filteredParticipants.length - 20} autres collaborateurs
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default function PerformancePageV2() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isFree, isPro, isSuperAdmin, loading: planLoading } = usePlan();
  
  // Get tab from URL parameter or default to 'overview'
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'overview');
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Sync tab with URL parameter
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'campaigns', 'collaborators'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, [user?.companyId]);

  const fetchData = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    
    try {
      const [campaignsRes, employeesRes, deptsRes] = await Promise.all([
        api.get(`/campaigns?company_id=${user.companyId}`),
        api.get(`/employees?companyId=${user.companyId}`),
        api.get(`/departments?companyId=${user.companyId}`)
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setEmployees(employeesRes.data.employees || employeesRes.data || []);
      setDepartments(deptsRes.data.departments || deptsRes.data || []);
      
    } catch (error) {
      console.error('Error fetching performance data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // KPI calculations
  const kpiData = useMemo(() => {
    const drafts = campaigns.filter(c => c.status === 'draft').length;
    const active = campaigns.filter(c => c.status === 'active').length;
    const closed = campaigns.filter(c => c.status === 'closed').length;
    const cancelled = campaigns.filter(c => c.status === 'cancelled').length;
    
    // Use participantsCount from backend (supports both old and new format)
    const totalParticipants = campaigns.reduce((sum, c) => 
      sum + (c.participantsCount || c.participants?.length || 0), 0);
    
    // Use completedCount from backend or stats.completed
    const completedEvals = campaigns.reduce((sum, c) => 
      sum + (c.completedCount || c.stats?.completed || 0), 0);
    
    // Use progress from backend if available
    const avgProgress = campaigns.filter(c => c.status === 'active').length > 0
      ? Math.round(
          campaigns.filter(c => c.status === 'active')
            .reduce((sum, c) => sum + (c.progress || 0), 0) / 
          campaigns.filter(c => c.status === 'active').length
        )
      : 0;
    
    const completionRate = totalParticipants > 0 
      ? Math.round((completedEvals / totalParticipants) * 100) 
      : avgProgress;
    
    // Last activity - check for recent completions
    const lastActivity = completedEvals > 0 ? 'Aujourd\'hui' : 
                         campaigns.length > 0 ? 'En cours' : 'Aucune';
    
    return { drafts, active, closed, cancelled, completionRate, lastActivity, totalParticipants };
  }, [campaigns]);

  // Filter campaigns
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!c.name.toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [campaigns, statusFilter, searchQuery]);

  // Handlers
  const handleActivateCampaign = async (campaignId, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/campaigns/${campaignId}/activate?company_id=${user?.companyId}`);
      toast.success('Campagne activée avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    }
  };

  const handleCloseCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/close?company_id=${user.companyId}`);
      toast.success('Campagne clôturée avec succès');
      fetchData();
    } catch (error) {
      console.error('Close campaign error:', error);
      toast.error('Erreur lors de la clôture');
    }
  };

  const handleDeleteClick = (campaign, e) => {
    e?.stopPropagation();
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/campaigns/${campaignToDelete.id}?company_id=${user.companyId}`);
      toast.success('Campagne supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    }
  };

  const handleRelance = (campaign) => {
    toast.info(`Relance envoyée aux managers de "${campaign.name}"`);
  };

  const handleViewDetails = (campaign) => {
    navigate(`/performance/campaign/${campaign.id}`);
  };

  // Redirect employees
  if (user?.role === 'Employé') {
    navigate('/my-evaluations', { replace: true });
    return null;
  }

  if (loading || planLoading) {
    return (
      <DashboardLayout title="Performance">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Performance">
      <div className="space-y-6">
        {/* ZONE 1: Header Stratégique */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Target className="h-7 w-7 text-amber-500" />
                Cockpit Performance
              </h1>
              <GlobalStatusBadge campaigns={campaigns} />
            </div>
            <p className="text-slate-500 mt-1">
              Pilotez vos campagnes d'évaluation • {employees.length} collaborateurs
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle campagne
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard
            title="Brouillons"
            value={kpiData.drafts}
            icon={FileText}
            color="text-slate-600"
            bgColor="bg-slate-100"
            onClick={() => setStatusFilter('draft')}
            active={statusFilter === 'draft'}
          />
          <KPICard
            title="Actives"
            value={kpiData.active}
            icon={Play}
            color="text-green-600"
            bgColor="bg-green-100"
            subtitle={kpiData.active > 0 ? 'En cours' : null}
            onClick={() => setStatusFilter('active')}
            active={statusFilter === 'active'}
          />
          <KPICard
            title="Terminées"
            value={kpiData.closed}
            icon={CheckCircle2}
            color="text-blue-600"
            bgColor="bg-blue-100"
            onClick={() => setStatusFilter('closed')}
            active={statusFilter === 'closed'}
          />
          <KPICard
            title="Taux complétion"
            value={kpiData.totalParticipants > 0 ? `${kpiData.completionRate}%` : '-'}
            icon={Gauge}
            color="text-purple-600"
            bgColor="bg-purple-100"
          />
          <KPICard
            title="Participants"
            value={kpiData.totalParticipants}
            icon={Users}
            color="text-indigo-600"
            bgColor="bg-indigo-100"
          />
          <KPICard
            title="Dernière activité"
            value={kpiData.lastActivity}
            icon={Activity}
            color="text-amber-600"
            bgColor="bg-amber-100"
          />
        </div>

        {/* ZONE 2: AI Insights Card */}
        <AIPerformanceInsights 
          campaigns={campaigns} 
          employees={employees} 
          isPro={isPro || isSuperAdmin}
          onCreateRecommended={() => setShowCreateDialog(true)}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="overview" className="gap-2">
              <PieChart className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="gap-2">
              <Target className="w-4 h-4" />
              Campagnes ({campaigns.length})
            </TabsTrigger>
            <TabsTrigger value="collaborators" className="gap-2">
              <Users className="w-4 h-4" />
              Suivi détaillé
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab - Campaign Cards */}
          <TabsContent value="overview" className="mt-6">
            {/* Filter bar */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher une campagne..." 
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('all')}
              >
                Toutes
              </Button>
              <Button 
                variant={statusFilter === 'active' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('active')}
                className={statusFilter === 'active' ? 'bg-green-600' : ''}
              >
                Actives
              </Button>
              <Button 
                variant={statusFilter === 'draft' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('draft')}
              >
                Brouillons
              </Button>
            </div>

            {filteredCampaigns.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {campaigns.length === 0 ? 'Aucune campagne créée' : 'Aucune campagne trouvée'}
                  </h3>
                  <p className="text-slate-500 mb-4">
                    {campaigns.length === 0 
                      ? 'Créez votre première campagne d\'évaluation pour commencer'
                      : 'Modifiez vos filtres pour voir plus de résultats'
                    }
                  </p>
                  {campaigns.length === 0 && (
                    <Button onClick={() => setShowCreateDialog(true)} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une campagne
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredCampaigns.map(campaign => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    employees={employees}
                    onActivate={handleActivateCampaign}
                    onClose={handleCloseCampaign}
                    onDelete={handleDeleteClick}
                    onRelance={handleRelance}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Campaigns Tab - Table View */}
          <TabsContent value="campaigns" className="mt-6">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Campagne</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Période</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>Progression</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Aucune campagne
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.map(campaign => {
                      const total = campaign.participants?.length || 0;
                      const completed = campaign.stats?.completed || Math.floor(total * 0.6);
                      const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
                      const isLate = campaign.status === 'active' && new Date(campaign.endDate) < new Date();
                      
                      return (
                        <TableRow key={campaign.id} className="hover:bg-slate-50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${isLate ? 'bg-red-500' : STATUS_CONFIG[campaign.status]?.dotColor}`} />
                              <span className="font-medium">{campaign.name}</span>
                              {isLate && <AlertTriangle className="w-4 h-4 text-red-500" />}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={CAMPAIGN_TYPE[getCampaignType(campaign)]?.color}>
                              {CAMPAIGN_TYPE[getCampaignType(campaign)]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={isLate ? STATUS_CONFIG.late.color : STATUS_CONFIG[campaign.status]?.color}>
                              {isLate ? 'En retard' : STATUS_CONFIG[campaign.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(campaign.startDate).toLocaleDateString('fr-FR')} - {new Date(campaign.endDate).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{total}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 w-24">
                              <Progress value={progress} className="h-2" />
                              <span className="text-xs w-8">{progress}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              {campaign.status === 'draft' && (
                                <Button size="sm" variant="ghost" className="text-green-600" onClick={(e) => handleActivateCampaign(campaign.id, e)}>
                                  <Play className="w-4 h-4" />
                                </Button>
                              )}
                              <Link to={`/performance/campaign/${campaign.id}`}>
                                <Button size="sm" variant="ghost">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem><Copy className="w-4 h-4 mr-2" />Dupliquer</DropdownMenuItem>
                                  <DropdownMenuItem><Download className="w-4 h-4 mr-2" />Export PDF</DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-red-600" onClick={(e) => handleDeleteClick(campaign, e)}>
                                    <Trash2 className="w-4 h-4 mr-2" />Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Collaborators Tab */}
          <TabsContent value="collaborators" className="mt-6">
            <CollaboratorsTable 
              campaigns={campaigns}
              employees={employees}
              departments={departments}
            />
          </TabsContent>
        </Tabs>

        {/* Campaign Wizard Dialog */}
        <CampaignWizard
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSuccess={() => {
            setShowCreateDialog(false);
            fetchData();
          }}
          companyId={user?.companyId}
          userId={user?.id}
          departments={departments}
          employees={employees}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer la campagne ?</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer la campagne "{campaignToDelete?.name}" ?
                Cette action est irréversible et supprimera toutes les évaluations associées.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
